import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  X,
  MoreHorizontal,
  Pencil,
  Trash2,
  FileText,
  Star,
  ChevronDown,
  ChevronUp,
  Clock,
  History,
  AlertTriangle,
  Check,
  Ship,
  Plane,
  Truck,
  Train,
  Globe,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PageHeader, MetricCard, KPIStrip } from '@/components/common';
import {
  termsApi,
  TermGroup,
  TermTemplate,
  ShipmentType,
  CreateTermGroupDto,
  CreateTermDto,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { format } from 'date-fns';

// Constants
const SHIPMENT_TYPES: { value: ShipmentType; label: string; icon: React.ReactNode }[] = [
  { value: 'AIR', label: 'Air', icon: <Plane className="w-4 h-4" /> },
  { value: 'OCEAN_FCL', label: 'Ocean FCL', icon: <Ship className="w-4 h-4" /> },
  { value: 'OCEAN_LCL', label: 'Ocean LCL', icon: <Ship className="w-4 h-4" /> },
  { value: 'FTL', label: 'Truck FTL', icon: <Truck className="w-4 h-4" /> },
  { value: 'LTL', label: 'Truck LTL', icon: <Truck className="w-4 h-4" /> },
  { value: 'RAIL', label: 'Rail', icon: <Train className="w-4 h-4" /> },
  { value: 'INTERMODAL', label: 'Intermodal', icon: <Globe className="w-4 h-4" /> },
];

export default function TermsConditions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<TermGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedTerms, setExpandedTerms] = useState<Set<string>>(new Set());

  // Sheet/Dialog states
  const [groupSheetOpen, setGroupSheetOpen] = useState(false);
  const [termSheetOpen, setTermSheetOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<TermGroup | null>(null);
  const [editingTerm, setEditingTerm] = useState<TermTemplate | null>(null);
  const [selectedGroupForTerm, setSelectedGroupForTerm] = useState<TermGroup | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    type: 'group' | 'term';
    item: TermGroup | TermTemplate;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [groupFormData, setGroupFormData] = useState<CreateTermGroupDto>({
    name: '',
    description: '',
    shipmentTypes: [],
    regions: [],
  });

  const [termFormData, setTermFormData] = useState<CreateTermDto>({
    code: '',
    title: '',
    content: '',
    summary: '',
    isRequired: false,
    effectiveFrom: '',
    effectiveUntil: '',
  });

  // Parse filters from URL
  const searchQuery = searchParams.get('search') || '';
  const shipmentTypeFilter = searchParams.get('shipmentType') as ShipmentType | null;

  const setFilter = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = searchQuery || shipmentTypeFilter;

  // Load data
  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await termsApi.getAllGroups({
        search: searchQuery || undefined,
        shipmentType: shipmentTypeFilter || undefined,
      });
      setGroups(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, shipmentTypeFilter]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // Stats
  const totalGroups = groups.length;
  const activeGroups = groups.filter((g) => g.isActive).length;
  const totalTerms = groups.reduce((sum, g) => sum + (g.terms?.length || 0), 0);
  const defaultGroup = groups.find((g) => g.isDefault);

  // Toggle functions
  const toggleGroupExpand = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleTermExpand = (termId: string) => {
    setExpandedTerms((prev) => {
      const next = new Set(prev);
      if (next.has(termId)) {
        next.delete(termId);
      } else {
        next.add(termId);
      }
      return next;
    });
  };

  // Group handlers
  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupFormData({
      name: '',
      description: '',
      shipmentTypes: [],
      regions: [],
    });
    setGroupSheetOpen(true);
  };

  const handleEditGroup = (group: TermGroup) => {
    setEditingGroup(group);
    setGroupFormData({
      name: group.name,
      description: group.description || '',
      shipmentTypes: group.shipmentTypes || [],
      regions: group.regions || [],
    });
    setGroupSheetOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!groupFormData.name) {
      toast.error('Please provide a group name');
      return;
    }

    setSaving(true);
    try {
      if (editingGroup) {
        await termsApi.updateGroup(editingGroup.id, groupFormData);
        toast.success('Term group updated successfully');
      } else {
        await termsApi.createGroup(groupFormData);
        toast.success('Term group created successfully');
      }
      setGroupSheetOpen(false);
      await loadGroups();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (group: TermGroup) => {
    try {
      await termsApi.setDefaultGroup(group.id);
      toast.success('Default group updated');
      await loadGroups();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Term handlers
  const handleCreateTerm = (group: TermGroup) => {
    setSelectedGroupForTerm(group);
    setEditingTerm(null);
    setTermFormData({
      code: '',
      title: '',
      content: '',
      summary: '',
      isRequired: false,
      effectiveFrom: '',
      effectiveUntil: '',
    });
    setTermSheetOpen(true);
  };

  const handleEditTerm = (term: TermTemplate, group: TermGroup) => {
    setSelectedGroupForTerm(group);
    setEditingTerm(term);
    setTermFormData({
      code: term.code,
      title: term.title,
      content: term.content,
      summary: term.summary || '',
      isRequired: term.isRequired,
      effectiveFrom: term.effectiveFrom || '',
      effectiveUntil: term.effectiveUntil || '',
    });
    setTermSheetOpen(true);
  };

  const handleSaveTerm = async () => {
    if (!termFormData.code || !termFormData.title || !termFormData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!selectedGroupForTerm) return;

    setSaving(true);
    try {
      if (editingTerm) {
        await termsApi.updateTerm(editingTerm.id, termFormData);
        toast.success('Term updated successfully');
      } else {
        await termsApi.createTerm(selectedGroupForTerm.id, termFormData);
        toast.success('Term created successfully');
      }
      setTermSheetOpen(false);
      await loadGroups();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === 'group') {
        await termsApi.deleteGroup((itemToDelete.item as TermGroup).id);
        toast.success('Term group deleted successfully');
      } else {
        await termsApi.deleteTerm((itemToDelete.item as TermTemplate).id);
        toast.success('Term deleted successfully');
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      await loadGroups();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const toggleShipmentType = (type: ShipmentType) => {
    setGroupFormData((prev) => ({
      ...prev,
      shipmentTypes: prev.shipmentTypes?.includes(type)
        ? prev.shipmentTypes.filter((t) => t !== type)
        : [...(prev.shipmentTypes || []), type],
    }));
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Terms & Conditions"
        description="Contract clause sets applied to quotations and awards, held as numbered versions."
        actions={
          <Button onClick={() => navigate('/tms/terms/new')}>
            <Plus className="w-4 h-4 mr-2" />
            New Group
          </Button>
        }
      />

      {/* KPIs */}
      <KPIStrip columns={4}>
        <MetricCard
          label="Total Groups"
          value={totalGroups}
          icon={<FileText className="w-5 h-5" />}
        />
        <MetricCard
          label="Active Groups"
          value={activeGroups}
          icon={<Check className="w-5 h-5 text-status-success" />}
        />
        <MetricCard
          label="Total Terms"
          value={totalTerms}
          icon={<FileText className="w-5 h-5 text-status-info" />}
        />
        <MetricCard
          label="Default Group"
          value={defaultGroup?.name || 'None'}
          icon={<Star className="w-5 h-5 text-status-warning" />}
        />
      </KPIStrip>

      {/* Filters */}
      <div className={cn(PANEL, 'p-4')}>
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search groups or terms..."
              value={searchQuery}
              onChange={(e) => setFilter('search', e.target.value || undefined)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setFilter('search', undefined)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Shipment Type Filter */}
          <Select
            value={shipmentTypeFilter || 'ALL_TYPES'}
            onValueChange={(v) => setFilter('shipmentType', v === 'ALL_TYPES' ? undefined : v)}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL_TYPES">All Types</SelectItem>
              {SHIPMENT_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-center gap-2">
                    {type.icon}
                    {type.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-destructive hover:text-destructive"
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Groups */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : groups.length > 0 ? (
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.id} className={cn(PANEL, 'overflow-hidden')}>
              {/* Group Header */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                onClick={() => toggleGroupExpand(group.id)}
              >
                <div className="flex items-center gap-4">
                  <button className="p-1 hover:bg-muted rounded">
                    {expandedGroups.has(group.id) ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold">{group.name}</h3>
                      {group.isDefault && (
                        <Badge className="bg-status-warning-subtle text-status-warning-foreground">
                          <Star className="w-3 h-3 mr-1" />
                          Default
                        </Badge>
                      )}
                      {!group.isActive && (
                        <Badge variant="outline" className="bg-muted text-muted-foreground">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    {group.description && (
                      <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      {group.shipmentTypes?.map((type) => {
                        const typeInfo = SHIPMENT_TYPES.find((t) => t.value === type);
                        return (
                          <Badge key={type} variant="outline" className="text-xs">
                            {typeInfo?.icon}
                            <span className="ml-1">{typeInfo?.label || type}</span>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold">{group.terms?.length || 0}</div>
                    <div className="text-xs text-muted-foreground">terms</div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" aria-label="More actions">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditGroup(group)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Group
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleCreateTerm(group)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Term
                      </DropdownMenuItem>
                      {!group.isDefault && (
                        <DropdownMenuItem onClick={() => handleSetDefault(group)}>
                          <Star className="w-4 h-4 mr-2" />
                          Set as Default
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          setItemToDelete({ type: 'group', item: group });
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Group
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Expanded Terms List */}
              {expandedGroups.has(group.id) && (
                <div className="border-t">
                  {group.terms && group.terms.length > 0 ? (
                    <div className="divide-y">
                      {group.terms.map((term) => (
                        <div key={term.id} className="hover:bg-muted/30">
                          {/* Term Header */}
                          <div
                            className="px-4 py-3 flex items-center justify-between cursor-pointer"
                            onClick={() => toggleTermExpand(term.id)}
                          >
                            <div className="flex items-center gap-3">
                              <button className="p-1 hover:bg-muted rounded">
                                {expandedTerms.has(term.id) ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>
                              <Badge variant="outline" className="font-mono text-xs">
                                {term.code}
                              </Badge>
                              <div>
                                <div className="font-medium">{term.title}</div>
                                {term.summary && (
                                  <p className="text-sm text-muted-foreground line-clamp-1">
                                    {term.summary}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <History className="w-3.5 h-3.5" />v{term.version}
                              </div>
                              {term.isRequired && (
                                <Badge className="bg-status-destructive-subtle text-status-destructive-foreground text-xs">
                                  Required
                                </Badge>
                              )}
                              {term.effectiveFrom && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Clock className="w-3.5 h-3.5" />
                                  {format(new Date(term.effectiveFrom), 'MMM d, yyyy')}
                                </div>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="More actions"
                                    className="h-8 w-8"
                                  >
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleEditTerm(term, group)}>
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit Term
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => {
                                      setItemToDelete({ type: 'term', item: term });
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Term
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>

                          {/* Expanded Term Content */}
                          {expandedTerms.has(term.id) && (
                            <div className="px-12 pb-4">
                              <div className="bg-muted/50 rounded-lg p-4">
                                <pre className="whitespace-pre-wrap text-sm font-sans">
                                  {term.content}
                                </pre>
                              </div>
                              {(term.effectiveFrom || term.effectiveUntil) && (
                                <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                                  {term.effectiveFrom && (
                                    <span>
                                      Effective from:{' '}
                                      {format(new Date(term.effectiveFrom), 'MMM d, yyyy')}
                                    </span>
                                  )}
                                  {term.effectiveUntil && (
                                    <span>
                                      Until: {format(new Date(term.effectiveUntil), 'MMM d, yyyy')}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No terms in this group</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => handleCreateTerm(group)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Term
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={cn(PANEL, 'p-12 text-center')}>
          <AlertTriangle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-base font-semibold mb-2">No Term Groups Found</h3>
          <p className="text-muted-foreground mb-4">
            {hasActiveFilters
              ? 'No groups match your filters.'
              : 'Get started by creating your first terms group.'}
          </p>
          {!hasActiveFilters && (
            <Button onClick={handleCreateGroup}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Group
            </Button>
          )}
        </div>
      )}

      {/* Create/Edit Group Sheet */}
      <Sheet open={groupSheetOpen} onOpenChange={setGroupSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingGroup ? 'Edit Term Group' : 'New Term Group'}</SheetTitle>
            <SheetDescription>
              {editingGroup
                ? 'Update the term group details below.'
                : 'Create a new group for organizing terms and conditions.'}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-6">
            <div className="space-y-2">
              <Label htmlFor="groupName">Group Name *</Label>
              <Input
                id="groupName"
                value={groupFormData.name}
                onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                placeholder="e.g., Ocean Freight Terms"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="groupDescription">Description</Label>
              <Textarea
                id="groupDescription"
                value={groupFormData.description || ''}
                onChange={(e) =>
                  setGroupFormData({ ...groupFormData, description: e.target.value })
                }
                placeholder="Optional description..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Shipment Types</Label>
              <div className="flex flex-wrap gap-2">
                {SHIPMENT_TYPES.map((type) => (
                  <Badge
                    key={type.value}
                    variant={
                      groupFormData.shipmentTypes?.includes(type.value) ? 'default' : 'outline'
                    }
                    className="cursor-pointer"
                    onClick={() => toggleShipmentType(type.value)}
                  >
                    {type.icon}
                    <span className="ml-1">{type.label}</span>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => setGroupSheetOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGroup} disabled={saving}>
              {saving ? 'Saving...' : editingGroup ? 'Update' : 'Create'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Create/Edit Term Sheet */}
      <Sheet open={termSheetOpen} onOpenChange={setTermSheetOpen}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingTerm ? 'Edit Term' : 'New Term'}</SheetTitle>
            <SheetDescription>
              {editingTerm
                ? `Editing term in ${selectedGroupForTerm?.name}`
                : `Adding term to ${selectedGroupForTerm?.name}`}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="termCode">Code *</Label>
                <Input
                  id="termCode"
                  value={termFormData.code}
                  onChange={(e) =>
                    setTermFormData({ ...termFormData, code: e.target.value.toUpperCase() })
                  }
                  placeholder="e.g., PAYMENT_TERMS"
                  disabled={!!editingTerm}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="termTitle">Title *</Label>
                <Input
                  id="termTitle"
                  value={termFormData.title}
                  onChange={(e) => setTermFormData({ ...termFormData, title: e.target.value })}
                  placeholder="e.g., Payment Terms"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="termSummary">Summary</Label>
              <Input
                id="termSummary"
                value={termFormData.summary || ''}
                onChange={(e) => setTermFormData({ ...termFormData, summary: e.target.value })}
                placeholder="Brief summary for display..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="termContent">Content *</Label>
              <Textarea
                id="termContent"
                value={termFormData.content}
                onChange={(e) => setTermFormData({ ...termFormData, content: e.target.value })}
                placeholder="Full term content..."
                rows={10}
                className="font-mono text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="effectiveFrom">Effective From</Label>
                <Input
                  id="effectiveFrom"
                  type="date"
                  value={termFormData.effectiveFrom || ''}
                  onChange={(e) =>
                    setTermFormData({ ...termFormData, effectiveFrom: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="effectiveUntil">Effective Until</Label>
                <Input
                  id="effectiveUntil"
                  type="date"
                  value={termFormData.effectiveUntil || ''}
                  onChange={(e) =>
                    setTermFormData({ ...termFormData, effectiveUntil: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="isRequired"
                checked={termFormData.isRequired}
                onCheckedChange={(checked) =>
                  setTermFormData({ ...termFormData, isRequired: !!checked })
                }
              />
              <Label htmlFor="isRequired" className="font-normal">
                This term is required (must be accepted)
              </Label>
            </div>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => setTermSheetOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTerm} disabled={saving}>
              {saving ? 'Saving...' : editingTerm ? 'Update' : 'Create'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-status-destructive-subtle rounded-full">
                <Trash2 className="w-5 h-5 text-status-destructive-foreground" />
              </div>
              Delete {itemToDelete?.type === 'group' ? 'Term Group' : 'Term'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium">
                {itemToDelete
                  ? itemToDelete.type === 'group'
                    ? (itemToDelete.item as TermGroup).name
                    : (itemToDelete.item as TermTemplate).title
                  : ''}
              </span>
              ?{' '}
              {itemToDelete?.type === 'group' && 'This will also delete all terms in this group. '}
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
